import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import {
  usePolls,
  useCreatePoll,
  useVotePoll,
  useClosePoll,
  useDeletePoll,
} from "@/application/hooks/usePolls";
import type { VotingPanelContainerProps } from "./types";
import { VotingPanel } from "./VotingPanel";

/**
 * VotingPanel container component.
 * Handles data fetching, mutations, and state management.
 * Passes processed data to the VotingPanel presenter.
 */
export function VotingPanelContainer({ tripId }: VotingPanelContainerProps) {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();
  const [isCreating, setIsCreating] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newOptions, setNewOptions] = useState(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [deadline, setDeadline] = useState("");
  const [expandedPolls, setExpandedPolls] = useState<Set<string>>(new Set());

  const { data: polls = [], isLoading } = usePolls(tripId);
  const createMutation = useCreatePoll(tripId);
  const voteMutation = useVotePoll(tripId);
  const closeMutation = useClosePoll(tripId);
  const deleteMutation = useDeletePoll(tripId);

  const handleAddOption = () => {
    if (newOptions.length < 10) {
      setNewOptions([...newOptions, ""]);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (newOptions.length > 2) {
      setNewOptions(newOptions.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const updated = [...newOptions];
    updated[index] = value;
    setNewOptions(updated);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.trim() || newOptions.filter((o) => o.trim()).length < 2) return;

    try {
      await createMutation.mutateAsync({
        question: newQuestion.trim(),
        options: newOptions.filter((o) => o.trim()),
        allowMultiple,
        deadline: deadline ? new Date(deadline).toISOString() : undefined,
      });
      setIsCreating(false);
      setNewQuestion("");
      setNewOptions(["", ""]);
      setAllowMultiple(false);
      setDeadline("");
      toast.success(t("polls.createSuccess", "投票を作成しました"));
    } catch {
      toast.error(t("polls.createError", "投票の作成に失敗しました"));
    }
  };

  const handleVote = async (pollId: string, optionIndex: number) => {
    try {
      await voteMutation.mutateAsync({ pollId, data: { optionIndex } });
      toast.success(t("polls.voteSuccess", "投票しました"));
    } catch {
      toast.error(t("polls.voteError", "投票に失敗しました"));
    }
  };

  const handleClose = async (pollId: string) => {
    try {
      await closeMutation.mutateAsync(pollId);
      toast.success(t("polls.closeSuccess", "投票を締め切りました"));
    } catch {
      toast.error(t("polls.closeError", "投票の締め切りに失敗しました"));
    }
  };

  const handleDelete = async (pollId: string) => {
    const confirmed = await confirm({
      title: t("polls.deletePoll"),
      description: t("polls.confirmDelete"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(pollId);
      toast.success(t("polls.deleteSuccess", "投票を削除しました"));
    } catch {
      toast.error(t("polls.deleteError", "投票の削除に失敗しました"));
    }
  };

  const togglePollExpand = (pollId: string) => {
    setExpandedPolls((prev) => {
      const next = new Set(prev);
      if (next.has(pollId)) {
        next.delete(pollId);
      } else {
        next.add(pollId);
      }
      return next;
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewQuestion("");
    setNewOptions(["", ""]);
    setAllowMultiple(false);
    setDeadline("");
  };

  return (
    <VotingPanel
      polls={polls}
      isLoading={isLoading}
      isCreating={isCreating}
      newQuestion={newQuestion}
      newOptions={newOptions}
      allowMultiple={allowMultiple}
      deadline={deadline}
      expandedPolls={expandedPolls}
      isCreatingPoll={createMutation.isPending}
      isVoting={voteMutation.isPending}
      isClosing={closeMutation.isPending}
      isDeleting={deleteMutation.isPending}
      onNewQuestionChange={setNewQuestion}
      onOptionChange={handleOptionChange}
      onAddOption={handleAddOption}
      onRemoveOption={handleRemoveOption}
      onAllowMultipleChange={setAllowMultiple}
      onDeadlineChange={setDeadline}
      onCreate={handleCreate}
      onCancelCreate={handleCancelCreate}
      onStartCreate={() => setIsCreating(true)}
      onVote={handleVote}
      onClose={handleClose}
      onDelete={handleDelete}
      onToggleExpand={togglePollExpand}
      dialogProps={dialogProps}
    />
  );
}
